import type { Anomaly } from '@soc/shared';
import { SEVERITIES } from '@soc/shared';
import { useGetRulesQuery } from '../../app/api';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { countBySeverity, useAllAlerts } from '../alerts/selectors';
import { sevColor } from '../../lib/severity';
import { RuleIcon } from '../../lib/ruleIcons';
import { highlightAlert } from './mapUiSlice';
import { severityChipStyle } from './AlertPopupBody';
import { InvestigateMemberButton } from './InvestigateMemberButton';

/** Contents of the anchored popup for a cluster of same-rule alerts. */
export function ClusterPopupBody({ ruleId, alertIds }: { ruleId: string; alertIds: string[] }) {
  const dispatch = useAppDispatch();
  const items = useAllAlerts();
  const highlightedId = useAppSelector((s) => s.mapUi.highlightedAlertId);
  const { data: rules } = useGetRulesQuery();

  // Preserve cluster order (alertIds[0] first) rather than store order.
  const byId = new Map(items.map((a) => [a.id, a] as const));
  const members = alertIds.map((id) => byId.get(id)).filter((a): a is Anomaly => !!a);
  const rule = rules?.items.find((r) => r.id === ruleId);
  const counts = countBySeverity(members);
  const ruleName = rule?.name ?? members[0]?.ruleName ?? ruleId;
  const subtitle = rule?.description ?? `${members.length} detection${members.length === 1 ? '' : 's'}`;

  return (
    <div style={{ minWidth: 210, maxWidth: 250 }}>
      {/* header: rule identity + detection subtitle left, severity counts right */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <span style={{ display: 'inline-flex', color: 'var(--text-secondary)', flex: 'none', marginTop: 1 }}>
          <RuleIcon ruleId={ruleId} size={15} />
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, flex: 1 }}>
          <span style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {ruleName}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {subtitle}
          </span>
        </span>
        <span style={{ display: 'flex', gap: 4, flex: 'none' }}>
          {SEVERITIES.filter((s) => counts[s] > 0).map((s) => (
            <span key={s} style={severityChipStyle(s)}>
              {counts[s]}
            </span>
          ))}
        </span>
      </div>

      {/* lightweight references — click one to point the live feed at that alert */}
      <div
        className="scrl"
        style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 210, overflowY: 'auto', margin: '0 -2px', paddingRight: 2 }}
      >
        {members.map((a) => {
          const color = sevColor(a.severity);
          const isHi = a.id === highlightedId;
          return (
            <div key={a.id} style={{ display: 'flex', alignItems: 'stretch', gap: 5 }}>
              <button
                type="button"
                onClick={() => dispatch(highlightAlert(a.id))}
                title="Show in live feed"
                className="cluster-ref"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flex: 1,
                  minWidth: 0,
                  textAlign: 'left',
                  padding: '6px 8px',
                  background: 'var(--surface-card)',
                  border: `1px solid ${isHi ? color : 'var(--border-card)'}`,
                  borderRadius: 6,
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                <span style={{ display: 'flex', flexDirection: 'column', gap: 0, flex: 1, minWidth: 0 }}>
                  <span className="mono" style={{ fontSize: 12, lineHeight: 1.45, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: isHi ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                    {a.towerId}
                  </span>
                  <span className="mono" style={{ fontSize: 11, lineHeight: 1.35, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color }}>
                    {a.value}
                  </span>
                </span>
                <span className="cluster-ref__go" style={{ fontSize: 13, color: 'var(--text-muted)', flex: 'none' }}>→</span>
              </button>
              <InvestigateMemberButton alert={a} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
